import {
  FilterOption,
  TooltipOption,
  viewLayoutSchema,
} from 'features/knowledgeDocumentViewer/types'
import { z } from 'zod'

const STORAGE_KEY_PREFIX = 'gecko_viewer_prefs:v1'
const CURRENT_VERSION = 1

const LEGACY_UX_PREFS_PREFIX = 'gecko_docpage_ux'

function getStorageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}_${userId}`
}

const storedMinimapPreferenceSchema = z.enum(['auto', 'forced', 'dismissed'])

/** Stored `forced` meant always-on; treat it as auto (show only while zoomed). */
export function normalizeMinimapPreference(
  preference: z.infer<typeof storedMinimapPreferenceSchema> | undefined
): 'auto' | 'dismissed' {
  return preference === 'dismissed' ? 'dismissed' : 'auto'
}

const minimapPreferenceSchema = storedMinimapPreferenceSchema.transform(
  (preference) => normalizeMinimapPreference(preference)
)
export type MinimapPreference = z.infer<typeof minimapPreferenceSchema>

// Explicit booleans, not `tooltipOptions` membership: presence-means-enabled
// cannot express default-ON settings.
// `assetClickable` defaults rather than being required: preferences persisted
// before the split carry only `asset`, and a required key would fail the whole
// read and drop every other stored preference with it.
const linkHighlightOptionsSchema = z.object({
  asset: z.boolean(),
  assetClickable: z.boolean().default(true),
  docReference: z.boolean(),
})
export type LinkHighlightOptions = z.infer<typeof linkHighlightOptionsSchema>

const viewerPreferencesSchema = z.object({
  _v: z.number(),
  tooltipOptions: z.array(z.enum(TooltipOption)),
  filterOptions: z.enum(FilterOption),
  viewLayout: viewLayoutSchema,
  isSidebarCollapsed: z.boolean(),
  isThumbnailSidebarOpen: z.boolean(),
  thumbnailSidebarWidth: z.number().nullable(),
  minimapPreference: minimapPreferenceSchema,
  linkHighlights: linkHighlightOptionsSchema,
  showLiveProcessData: z.boolean(),
  graphMode: z.boolean(),
  photoMode: z.boolean(),
})

export type ViewerPreferences = z.infer<typeof viewerPreferencesSchema>
type ViewerPreferencesUpdate = Partial<Omit<ViewerPreferences, '_v'>>

export const VIEWER_PREFERENCES_DEFAULTS: ViewerPreferences = {
  _v: CURRENT_VERSION,
  tooltipOptions: [],
  filterOptions: FilterOption.ShowConfirmed,
  viewLayout: 'continuous',
  isSidebarCollapsed: true,
  isThumbnailSidebarOpen: false,
  thumbnailSidebarWidth: null,
  minimapPreference: 'auto',
  linkHighlights: { asset: true, assetClickable: true, docReference: true },
  showLiveProcessData: false,
  graphMode: false,
  photoMode: false,
}

export function effectiveLinkHighlights(
  linkHighlights: LinkHighlightOptions,
  photoMode: boolean
): LinkHighlightOptions {
  return photoMode
    ? { ...linkHighlights, asset: true, assetClickable: true }
    : linkHighlights
}

function setIfChanged(key: string, serialized: string): void {
  if (localStorage.getItem(key) === serialized) return
  localStorage.setItem(key, serialized)
}

function handleQuotaError(e: unknown, key: string): void {
  if (e instanceof DOMException && e.name === 'QuotaExceededError') {
    try {
      localStorage.removeItem(key)
    } catch {
      /* noop */
    }
  }
}

const legacyUXPreferencesSchema = z.object({
  _v: z.literal(CURRENT_VERSION),
  tooltipOptions: z.array(z.enum(TooltipOption)),
  filterOptions: z.enum(FilterOption),
  isSidebarCollapsed: z.boolean(),
  viewLayout: viewLayoutSchema.optional(),
})

function migrateFromSessionStorage(siteId: string): ViewerPreferencesUpdate {
  try {
    const legacyKey = `${LEGACY_UX_PREFS_PREFIX}_${siteId}`
    const raw = sessionStorage.getItem(legacyKey)
    if (!raw) return {}

    const result = legacyUXPreferencesSchema.safeParse(JSON.parse(raw))
    if (!result.success) return {}

    sessionStorage.removeItem(legacyKey)

    return {
      tooltipOptions: result.data.tooltipOptions,
      filterOptions: result.data.filterOptions,
      isSidebarCollapsed: result.data.isSidebarCollapsed,
      viewLayout: result.data.viewLayout,
    }
  } catch {
    return {}
  }
}

/**
 * Reads persisted viewer preferences from localStorage.
 * On first read (no stored data), attempts one-time migration from
 * legacy sessionStorage UX prefs using the provided `siteId`.
 */
const storedPreferencesSchema = viewerPreferencesSchema
  .partial()
  .extend({ _v: z.literal(CURRENT_VERSION) })

export function getViewerPreferences(
  userId: string,
  siteId?: string
): Partial<ViewerPreferences> | null {
  try {
    const key = getStorageKey(userId)
    const raw = localStorage.getItem(key)

    if (!raw && siteId) {
      const migrated = migrateFromSessionStorage(siteId)
      if (Object.keys(migrated).length > 0) {
        const data: ViewerPreferences = {
          ...VIEWER_PREFERENCES_DEFAULTS,
          ...migrated,
        }
        localStorage.setItem(key, JSON.stringify(data))
        return data
      }
      return null
    }

    if (!raw) return null
    const result = storedPreferencesSchema.safeParse(JSON.parse(raw))
    if (!result.success) return null
    return result.data
  } catch {
    return null
  }
}

export function updateViewerPreferences(
  userId: string,
  updates: ViewerPreferencesUpdate
): void {
  try {
    const key = getStorageKey(userId)
    const existing = getViewerPreferences(userId)
    const data: ViewerPreferences = {
      ...VIEWER_PREFERENCES_DEFAULTS,
      ...existing,
      ...updates,
    }
    setIfChanged(key, JSON.stringify(data))
  } catch (e) {
    handleQuotaError(e, getStorageKey(userId))
  }
}
